/**
 * Channel aspect-ratio buckets — Phase 1 (settings + Design modal only).
 *
 * Canonical frontend mirror of the backend constants in
 * api_gateway/models/tendv2_settings.py (ASPECT_BUCKETS, BUCKET_DIMENSIONS,
 * CHANNEL_PLATFORMS, DEFAULT_UNIVERSAL_CHANNEL_ASPECT). Resolution:
 * content's stamped aspect_ratio → brand sparse override → universal map →
 * code default. Consumed by the card preview, OverlayPreview, and (server
 * side) the overlay renderer via each content row's stamp.
 */

export type AspectBucket = '1:1' | '4:5' | '3:4' | '2:3' | '9:16' | '16:9';

/** Design-modal row order. */
export const ASPECT_BUCKETS: AspectBucket[] = ['1:1', '4:5', '3:4', '2:3', '9:16', '16:9'];

/** Phase-2 render canvas per bucket (surfaced as captions in the modal). */
export const BUCKET_DIMENSIONS: Record<AspectBucket, { width: number; height: number }> = {
  '1:1': { width: 1200, height: 1200 },
  '4:5': { width: 1080, height: 1350 },
  '3:4': { width: 1080, height: 1440 },
  '2:3': { width: 1000, height: 1500 },
  '9:16': { width: 1080, height: 1920 },
  '16:9': { width: 1200, height: 675 },
};

/** CSS aspect-ratio value per bucket. */
export const BUCKET_CSS: Record<AspectBucket, string> = {
  '1:1': '1 / 1',
  '4:5': '4 / 5',
  '3:4': '3 / 4',
  '2:3': '2 / 3',
  '9:16': '9 / 16',
  '16:9': '16 / 9',
};

export const DEFAULT_ASPECT_BUCKET: AspectBucket = '1:1';

/** Every channel TENDv2 creates content for. */
export const CHANNEL_PLATFORMS = [
  'twitter',
  'linkedin',
  'instagram',
  'facebook',
  'threads',
  'tiktok',
  'youtube_shorts',
  'bluesky',
  'reddit',
  'pinterest',
  'snapchat',
  'quora',
  'newsletter',
] as const;

export type ChannelPlatform = (typeof CHANNEL_PLATFORMS)[number];

/** Code fallback beneath the universal KV map — 2026 platform best practices. */
export const DEFAULT_UNIVERSAL_CHANNEL_ASPECT: Record<ChannelPlatform, AspectBucket> = {
  twitter: '1:1',
  bluesky: '1:1',
  reddit: '1:1',
  quora: '1:1',
  facebook: '4:5',
  instagram: '4:5',
  linkedin: '4:5',
  threads: '4:5',
  pinterest: '2:3',
  tiktok: '9:16',
  youtube_shorts: '9:16',
  snapchat: '9:16',
  newsletter: '16:9',
};

const isBucket = (v: unknown): v is AspectBucket =>
  typeof v === 'string' && (ASPECT_BUCKETS as string[]).includes(v);

/** Validate an arbitrary value (e.g. item.aspect_ratio from the API). */
export const asAspectBucket = (v: unknown): AspectBucket | null => (isBucket(v) ? v : null);

/** brand override → universal → code default → '1:1'. Null-tolerant maps. */
export function resolveChannelAspect(
  brandMap: Record<string, string> | null | undefined,
  universalMap: Record<string, string> | null | undefined,
  platform: string,
): AspectBucket {
  const p0 = (platform || '').toLowerCase();
  // schemas_v2 stores 'youtube'; the aspect maps key 'youtube_shorts'.
  const p = p0 === 'youtube' ? 'youtube_shorts' : p0;
  const fromBrand = brandMap?.[p];
  if (isBucket(fromBrand)) return fromBrand;
  const fromUniversal = universalMap?.[p];
  if (isBucket(fromUniversal)) return fromUniversal;
  const fallback = (DEFAULT_UNIVERSAL_CHANNEL_ASPECT as Record<string, AspectBucket>)[p];
  return fallback ?? DEFAULT_ASPECT_BUCKET;
}

/** Caption for the modal, e.g. "1080×1350". */
export const bucketDimsLabel = (bucket: AspectBucket): string => {
  const d = BUCKET_DIMENSIONS[bucket];
  return `${d.width}×${d.height}`;
};
